import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  IoPencilOutline, 
  IoTrashOutline, 
  IoChevronUpOutline, 
  IoChevronDownOutline,
  IoAddOutline,
  IoCheckmarkCircle,
  IoEllipseOutline
} from 'react-icons/io5';
import { RubricCategory, RubricCriterion } from '../../hooks/useRubric';
import { useDarkMode } from '../../context/DarkModeContext';
import RubricCriterionItem from './RubricCriterionItem';
import { useSaveEvidenceConfig } from '../../hooks/useEvidenceConfig';

export interface RubricCategoryWithProgress extends RubricCategory {
  items: RubricItem[];
  mappedGoals: string[];
  completedCount: number;
}

export interface RubricItem {
  id: string;
  text: string;
  hasEvidence: boolean;
  careerCardCount?: number;
  noteCount?: number;
}

interface RubricCategoryCardProps {
  category: RubricCategoryWithProgress;
  onUpdate: (updates: Partial<RubricCategory>) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onReorder: (direction: 'up' | 'down') => void;
  onAddCriterion: (text: string, description?: string) => Promise<boolean>;
  onUpdateCriterion: (criterionId: string, updates: Partial<RubricCriterion>) => Promise<boolean>;
  onDeleteCriterion: (criterionId: string) => Promise<boolean>;
  onUpdateEvidenceTarget?: (categoryId: string, target: number) => Promise<boolean>;
  isFirst: boolean;
  isLast: boolean;
  isEditingAny: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onQuickAdd?: (rubricItemId?: string, category?: string, description?: string) => void;
}

const RubricCategoryCard: React.FC<RubricCategoryCardProps> = ({
  category,
  onUpdate,
  onDelete,
  onReorder,
  onAddCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
  onUpdateEvidenceTarget,
  isFirst,
  isLast,
  isEditingAny,
  onEditStart,
  onEditEnd,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWeight, setEditWeight] = useState(0);
  const [editEvidenceTarget, setEditEvidenceTarget] = useState(3);
  const [isAddingCriterion, setIsAddingCriterion] = useState(false);
  const [newCriterionText, setNewCriterionText] = useState('');
  const [newCriterionDesc, setNewCriterionDesc] = useState('');
  const { updateCriterionTarget } = useSaveEvidenceConfig();

  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-900' : 'bg-white';
  const completedColor = darkMode ? 'text-green-400' : 'text-green-600';
  const incompleteColor = darkMode ? 'text-gray-500' : 'text-gray-400';

  const handleEditStart = () => {
    setIsEditing(true);
    setEditName(category.name);
    setEditDescription(category.description || '');
    setEditWeight(category.weight);
    setEditEvidenceTarget((category as any).default_evidence_target || 3);
    onEditStart();
  };

  const handleSave = async () => {
    if (!editName.trim()) return;

    const success = await onUpdate({
      name: editName.trim(),
      description: editDescription.trim(),
      weight: editWeight
    });

    if (success && onUpdateEvidenceTarget) {
      // Update evidence target separately if it changed
      const currentTarget = (category as any).default_evidence_target || 3;
      if (editEvidenceTarget !== currentTarget) {
        await onUpdateEvidenceTarget(category.id, editEvidenceTarget);
      }
    }

    if (success) {
      setIsEditing(false);
      onEditEnd();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName('');
    setEditDescription('');
    setEditWeight(0);
    onEditEnd();
  };

  const handleAddCriterion = async () => {
    if (!newCriterionText.trim()) return;

    const success = await onAddCriterion(
      newCriterionText.trim(),
      newCriterionDesc.trim() || undefined
    );

    if (success) {
      setNewCriterionText('');
      setNewCriterionDesc('');
      setIsAddingCriterion(false);
    }
  };

  const progressPercentage = category.items.length > 0 
    ? Math.round((category.completedCount / category.items.length) * 100)
    : 0;

  return (
    <motion.div
      layout
      className={`border rounded-lg p-5 ${borderColor} ${cardBg} ${
        isEditingAny && !isEditing ? 'opacity-50' : ''
      } transition-opacity`}
    >
      {isEditing ? (
        // Edit mode UI
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className={`flex-1 px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Category name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            />
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className={`text-xs ${mutedTextColor}`}>Weight:</span>
                <input
                  type="number"
                  value={editWeight}
                  onChange={(e) => setEditWeight(Math.max(0, Math.min(100, Number(e.target.value))))}
                  className={`w-16 px-2 py-1 rounded border ${borderColor} ${inputBg} ${textColor} text-center focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                  max="100"
                />
                <span className={textColor}>%</span>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-xs ${mutedTextColor}`}>Target:</span>
                <input
                  type="number"
                  value={editEvidenceTarget}
                  onChange={(e) => setEditEvidenceTarget(Math.max(1, Math.min(10, Number(e.target.value))))}
                  className={`w-12 px-2 py-1 rounded border ${borderColor} ${inputBg} ${textColor} text-center focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="1"
                  max="10"
                  title="Number of evidence cards required for 100% coverage"
                />
                <span className={`text-xs ${mutedTextColor}`}>cards</span>
              </div>
            </div>
          </div>

          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Description (optional)"
            className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} resize-none focus:outline-none focus:ring-2 focus:ring-blue-500`}
            rows={2}
          />

          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editName.trim()}
              className={`px-3 py-1 rounded text-sm ${
                editName.trim()
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        // Display mode UI
        <div>
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className={`font-medium ${textColor} text-lg`}>
                  {category.name}
                </h3>
                <span className={`text-sm font-medium ${mutedTextColor} bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded`}>
                  {category.weight}%
                </span>
                {category.mappedGoals.length > 0 && (
                  <span className={`text-sm ${mutedTextColor}`}>
                    • Linked to {category.mappedGoals.length} goal{category.mappedGoals.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {category.description && (
                <p className={`text-sm ${mutedTextColor} mt-1`}>{category.description}</p>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Reorder buttons */}
              <button
                onClick={() => onReorder('up')}
                disabled={isFirst}
                className={`p-1 rounded transition-colors ${
                  isFirst 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : `${mutedTextColor} hover:text-blue-500`
                }`}
                title="Move up"
              >
                <IoChevronUpOutline className="w-4 h-4" />
              </button>
              <button
                onClick={() => onReorder('down')}
                disabled={isLast}
                className={`p-1 rounded transition-colors ${
                  isLast 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : `${mutedTextColor} hover:text-blue-500`
                }`}
                title="Move down"
              >
                <IoChevronDownOutline className="w-4 h-4" />
              </button>

              {/* Edit button */}
              <button
                onClick={handleEditStart}
                className={`p-1 rounded transition-colors ${mutedTextColor} hover:text-blue-500`}
                title="Edit category"
              >
                <IoPencilOutline className="w-4 h-4" />
              </button>

              {/* Delete button */}
              <button
                onClick={onDelete}
                className={`p-1 rounded transition-colors ${mutedTextColor} hover:text-red-500`}
                title="Delete category"
              >
                <IoTrashOutline className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm ${mutedTextColor}`}>
                {category.completedCount} of {category.items.length} criteria met
              </span>
              <span className={`text-sm font-medium ${progressPercentage > 0 ? completedColor : mutedTextColor}`}>
                {progressPercentage}%
              </span>
            </div>
            <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
              <motion.div
                className="h-full bg-green-500"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Criteria list */}
          <div className="space-y-2">
            {category.items.map((item, index) => (
              <RubricCriterionItem
                key={item.id}
                criterion={item}
                categoryId={category.id}
                categoryName={category.name}
                onUpdate={onUpdateCriterion}
                onDelete={onDeleteCriterion}
                onUpdateEvidenceTarget={updateCriterionTarget}
                isEditingAny={isEditingAny || isAddingCriterion}
                onEditStart={onEditStart}
                onEditEnd={onEditEnd}
                onQuickAdd={onQuickAdd}
              />
            ))}

            {/* Add criterion form */}
            {isAddingCriterion ? (
              <div className="mt-3 p-3 border rounded-lg border-dashed border-blue-500 space-y-2">
                <input
                  value={newCriterionText}
                  onChange={(e) => setNewCriterionText(e.target.value)}
                  placeholder="Criterion text"
                  className={`w-full px-3 py-2 rounded border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddCriterion();
                    } else if (e.key === 'Escape') {
                      setIsAddingCriterion(false);
                      setNewCriterionText('');
                      setNewCriterionDesc('');
                    }
                  }}
                />
                <input
                  value={newCriterionDesc}
                  onChange={(e) => setNewCriterionDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className={`w-full px-3 py-2 rounded border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setIsAddingCriterion(false);
                      setNewCriterionText('');
                      setNewCriterionDesc('');
                    }}
                    className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCriterion}
                    disabled={!newCriterionText.trim()}
                    className={`px-3 py-1 rounded text-sm ${
                      newCriterionText.trim()
                        ? 'bg-blue-500 hover:bg-blue-600 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsAddingCriterion(true)}
                className={`w-full mt-2 p-2 rounded-lg border border-dashed ${borderColor} ${mutedTextColor} hover:border-blue-500 hover:text-blue-500 transition-colors text-sm flex items-center justify-center gap-1`}
              >
                <IoAddOutline className="w-4 h-4" />
                Add Criterion
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RubricCategoryCard;